import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Alert,
  Badge,
} from "reactstrap";
import Loader from "../../components/Common/Loader";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { GetData, PostData, DeleteData } from "../../services/api";
import Swal from "sweetalert2";

const CalendarProviders = () => {
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [savingProvider, setSavingProvider] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const [providers, setProviders] = useState([]);
  const [selectedProviderKey, setSelectedProviderKey] = useState("");

  const [providerConfigs, setProviderConfigs] = useState({});

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 5000);
  };

  const loadProviders = async () => {
    setLoadingProviders(true);
    try {
      const response = await GetData("/calendar/providers");
      const data = response?.data;
      const list = Array.isArray(data) ? data : [];
      setProviders(list);
      if (!selectedProviderKey && list.length > 0) {
        setSelectedProviderKey(list[0].key);
      }
    } catch (e) {
      console.error("Error loading providers", e);
      showMessage("danger", `Error: ${e.message || "Error desconocido"}`);
    } finally {
      setLoadingProviders(false);
    }
  };

  const loadProviderConfig = async (providerKey) => {
    if (!providerKey) return;
    try {
      const { data } = await GetData(`/calendar/providers/${providerKey}`);
      setProviderConfigs((prev) => ({ ...prev, [providerKey]: data || {} }));
    } catch (e) {
      console.error("Error loading provider config", e);
      // If provider doesn't exist, initialize empty config
      setProviderConfigs((prev) => ({ ...prev, [providerKey]: {} }));
    }
  };

  useEffect(() => {
    loadProviders();
    
    // Verificar si hay parámetros de URL después del OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const connected = urlParams.get('connected');
    const error = urlParams.get('error');
    
    if (connected === 'google_calendar') {
      Swal.fire({
        icon: "success",
        title: "¡Conexión exitosa!",
        text: "Google Calendar se ha conectado correctamente",
        confirmButtonColor: "#E9553E",
      });
      // Limpiar URL
      window.history.replaceState({}, document.title, window.location.pathname);
      loadProviders();
    } else if (error) {
      let errorMessage = "Error desconocido";
      if (error === 'oauth_cancelled') {
        errorMessage = "La conexión fue cancelada";
      } else if (error === 'oauth_failed') {
        errorMessage = "No se pudo conectar con Google Calendar";
      }
      Swal.fire({
        icon: "error",
        title: "Error de conexión",
        text: errorMessage,
        confirmButtonColor: "#E9553E",
      });
      // Limpiar URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (selectedProviderKey) {
      loadProviderConfig(selectedProviderKey);
    }
  }, [selectedProviderKey]);

  const handleChangeConfigField = (providerKey, field, value) => {
    setProviderConfigs((prev) => ({
      ...prev,
      [providerKey]: {
        ...(prev[providerKey] || {}),
        config: {
          ...(prev[providerKey]?.config || {}),
          [field]: value,
        },
      },
    }));
  };

  const handleSaveProvider = async (providerKey) => {
    if (!providerKey) return;
    const config = providerConfigs[providerKey]?.config || {};
    setSavingProvider(true);
    try {
      await PostData(`/calendar/providers/${providerKey}`, {
        config,
        providerName: providers.find((p) => p.key === providerKey)?.displayName,
      });

      Swal.fire({
        icon: "success",
        title: "¡Configuración guardada!",
        text: "El proveedor se ha configurado correctamente",
        confirmButtonColor: "#E9553E",
      });

      await loadProviders();
    } catch (e) {
      console.error("Error saving provider", e);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: e.message || "No se pudo guardar la configuración",
        confirmButtonColor: "#E9553E",
      });
    } finally {
      setSavingProvider(false);
    }
  };

  const handleTestConnection = async (providerKey) => {
    if (!providerKey) return;
    setTestingConnection(true);
    try {
      const response = await PostData(`/calendar/providers/${providerKey}/test`);
      const { success, message: msg } = response?.data || {};

      if (success) {
        Swal.fire({
          icon: "success",
          title: "¡Conexión exitosa!",
          text: msg || "La conexión con el proveedor funciona correctamente",
          confirmButtonColor: "#E9553E",
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error de conexión",
          text: msg || "No se pudo conectar con el proveedor",
          confirmButtonColor: "#E9553E",
        });
      }
    } catch (e) {
      console.error("Error testing connection", e);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: e.message || "No se pudo probar la conexión",
        confirmButtonColor: "#E9553E",
      });
    } finally {
      setTestingConnection(false);
    }
  };

  const handleConnectGoogle = async () => {
    try {
      const response = await GetData("/calendar/providers/google_calendar/oauth/authorize");
      if (response?.data?.authUrl) {
        // Redirigir a la URL de OAuth
        window.location.href = response.data.authUrl;
      }
    } catch (e) {
      console.error("Error connecting to Google", e);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: e.message || "No se pudo iniciar la conexión con Google",
        confirmButtonColor: "#E9553E",
      });
    }
  };

  const handleDeleteProvider = async (providerKey) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "¿Desconectar proveedor?",
      text: "Esta acción desconectará el proveedor. ¿Estás seguro?",
      showCancelButton: true,
      confirmButtonColor: "#E9553E",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sí, desconectar",
      cancelButtonText: "Cancelar",
    });

    if (result.isConfirmed) {
      try {
        await DeleteData(`/calendar/providers/${providerKey}`);

        Swal.fire({
          icon: "success",
          title: "¡Proveedor desconectado!",
          text: "El proveedor se ha desconectado correctamente",
          confirmButtonColor: "#E9553E",
        });

        await loadProviders();
        setSelectedProviderKey("");
      } catch (e) {
        console.error("Error deleting provider", e);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: e.message || "No se pudo desconectar el proveedor",
          confirmButtonColor: "#E9553E",
        });
      }
    }
  };

  const selectedProvider = providers.find((p) => p.key === selectedProviderKey);
  const selectedProviderConfig = providerConfigs[selectedProviderKey] || {};

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Proveedores de Calendario" breadcrumbItem="Configuración" />

        {message.text && (
          <Alert color={message.type} className="mt-3">
            {message.text}
          </Alert>
        )}

        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <h4 className="card-title mb-4">Proveedores Disponibles</h4>

                {loadingProviders ? (
                  <Loader message="Cargando proveedores..." />
                ) : (
                  <Row>
                    {providers.map((provider) => (
                      <Col md={6} key={provider.key} className="mb-3">
                        <Card
                          className={`cursor-pointer ${
                            selectedProviderKey === provider.key
                              ? "border-primary"
                              : ""
                          }`}
                          onClick={() => setSelectedProviderKey(provider.key)}
                          style={{ cursor: "pointer" }}
                        >
                          <CardBody>
                            <div className="d-flex justify-content-between align-items-center">
                              <div>
                                <h5 className="mb-1">
                                  <i className={provider.icon || "mdi mdi-calendar"} />{" "}
                                  {provider.displayName}
                                </h5>
                                <Badge
                                  color={
                                    provider.status === "connected"
                                      ? "success"
                                      : provider.status === "error"
                                      ? "danger"
                                      : "secondary"
                                  }
                                >
                                  {provider.status === "connected"
                                    ? "Conectado"
                                    : provider.status === "error"
                                    ? "Error"
                                    : "No configurado"}
                                </Badge>
                              </div>
                            </div>
                          </CardBody>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                )}

                {selectedProvider && (
                  <div className="mt-4">
                    <h5 className="mb-3">Configuración de {selectedProvider.displayName}</h5>

                    {/* OAuth Flow para Google Calendar */}
                    {selectedProvider.useOAuth ? (
                      <div>
                        {selectedProvider.status === "connected" ? (
                          <div>
                            <Alert color="success" className="mb-3">
                              <i className="mdi mdi-check-circle me-2" />
                              <strong>Conectado</strong> - {selectedProvider.displayName} está conectado correctamente.
                              Puedes sincronizar eventos automáticamente.
                            </Alert>
                            <Button
                              color="danger"
                              onClick={() => handleDeleteProvider(selectedProviderKey)}
                            >
                              <i className="mdi mdi-link-off me-2" />
                              Desconectar
                            </Button>
                          </div>
                        ) : (
                          <div>
                            <p className="mb-3">
                              Conecta tu cuenta de {selectedProvider.displayName} para sincronizar eventos automáticamente.
                              Serás redirigido a Google para autorizar el acceso.
                            </p>
                            <Button
                              color="primary"
                              size="lg"
                              onClick={handleConnectGoogle}
                            >
                              <i className={selectedProvider.icon || "mdi mdi-google"} />{" "}
                              Conectar con {selectedProvider.displayName}
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Configuración manual para otros proveedores (Zoom, etc.) */
                      <Form>
                        {selectedProvider.fields?.map((field) => (
                          <FormGroup key={field.name} className="mb-3">
                            <Label>
                              {field.label}
                              {field.help && (
                                <i
                                  className="mdi mdi-help-circle-outline ms-2"
                                  title={field.help}
                                  style={{ cursor: "help" }}
                                />
                              )}
                            </Label>
                            <Input
                              type={field.type === "secret" ? "password" : "text"}
                              placeholder={field.placeholder}
                              value={
                                selectedProviderConfig.config?.[field.name] || ""
                              }
                              onChange={(e) =>
                                handleChangeConfigField(
                                  selectedProviderKey,
                                  field.name,
                                  e.target.value
                                )
                              }
                            />
                            {field.help && (
                              <small className="form-text text-muted">
                                {field.help}
                              </small>
                            )}
                          </FormGroup>
                        ))}

                        <div className="d-flex gap-2">
                          <Button
                            color="primary"
                            onClick={() => handleSaveProvider(selectedProviderKey)}
                            disabled={savingProvider}
                          >
                            {savingProvider ? "Guardando..." : "Guardar Configuración"}
                          </Button>
                          <Button
                            color="info"
                            onClick={() => handleTestConnection(selectedProviderKey)}
                            disabled={testingConnection}
                          >
                            {testingConnection ? "Probando..." : "Probar Conexión"}
                          </Button>
                          {selectedProvider.status === "connected" && (
                            <Button
                              color="danger"
                              onClick={() => handleDeleteProvider(selectedProviderKey)}
                            >
                              Eliminar Configuración
                            </Button>
                          )}
                        </div>
                      </Form>
                    )}
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default CalendarProviders;

